import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Link from 'next/link'
import {
  Box,
  Button,
  ButtonProps,
  Collapse,
  useBreakpointValue,
} from '@chakra-ui/react'

import { Caption } from 'atoms/typography'
import ArrowDropUpIcon from 'components/icons/ArrowDropUpIcon'
import ArrowDropDownIcon from 'components/icons/ArrowDropDownIcon'
import useRouteEffect from 'lib/hooks/useRouteEffect'

import ProfileImage from './ProfileImage'

const buttonProps: ButtonProps = {
  variant: 'ghost',
  w: 'full',
  borderColor: 'grey',
  textTransform: 'none',
}

export interface ProfileProps {
  name: string
  imageUrl: string
  isStudent?: boolean
  compact?: boolean
}

const Profile: React.FC<ProfileProps> = ({
  name,
  imageUrl,
  isStudent,
  compact = false,
}) => {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)

  useRouteEffect(() => setIsOpen(false))

  const shouldCompact =
    (useBreakpointValue({ base: true, sm: false }) ?? false) && compact

  const profileImage = (
    <ProfileImage
      imageUrl={imageUrl}
      borderColor={isStudent ? 'gold' : 'cardinal'}
    />
  )

  const py = shouldCompact ? 0 : compact ? 0.5 : 2
  const px = shouldCompact ? 0 : compact ? 0 : 5

  return (
    <Box bg="white" display="inline-block" position="relative">
      <Button
        borderBottom="1px"
        {...buttonProps}
        borderColor={isOpen ? 'grey' : 'white'}
        py={py}
        pr={px}
        pl={px + 2}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={
          isOpen ? t('profile.closeAriaLabel') : t('profile.openAriaLabel')
        }
        leftIcon={shouldCompact ? undefined : profileImage}
        rightIcon={
          isOpen ? (
            <ArrowDropUpIcon color="cardinal" boxSize="6" />
          ) : (
            <ArrowDropDownIcon color="cardinal" boxSize="6" />
          )
        }
      >
        {shouldCompact ? (
          profileImage
        ) : (
          <Caption size="md" isTruncated pr="1">
            {name}
          </Caption>
        )}
      </Button>

      <Box minW="full" position="absolute" right="0" zIndex="profileDropdown">
        <Collapse
          in={isOpen}
          transition={{ enter: { duration: 0 }, exit: { duration: 0 } }}
        >
          <Box bg="white" boxShadow={isOpen ? 'md' : 'none'}>
            <Link
              href={process.env.NEXT_PUBLIC_IWITNESS_DASHBOARD_URL ?? ''}
              passHref
            >
              <Button
                as="a"
                {...buttonProps}
                px={compact ? 3 : 5}
                py="3"
                _hover={{ bg: 'background' }}
              >
                <Caption size="md">{t('profile.dashboardLink')}</Caption>
              </Button>
            </Link>
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}

export default Profile
